import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { DateTime } from "luxon";
import {
  BaseInput,
  Button,
  ButtonVariant,
  Modal,
  Select,
  Show
} from "shared/components";
import { Tabs, type Tab } from "shared/components/BaseTabs";
import { Label, LabelSize } from "shared/components/forms/Label";
import {
  lockboxesApi,
  Lockbox,
  AccessCode,
  AccessCodesResponse,
  GenerateLockboxCodePayload,
  LockboxCodeType,
  RentlyDeviceType,
  SENTRILOCK_CODE_DURATIONS
} from "api/landlord/lockboxes";
import { getApiErrorKey, getErrorMessage } from "utils/error";
import { VENDOR_COMPANY_LABELS } from "views/lockboxes/constants";
import { CodeDateField } from "./CodeDateField";
import {
  CodeTimezone,
  PAST_START_MESSAGE,
  buildGeneratePayload,
  StartTimeOption,
  codeWindowSummary,
  combineStart,
  defaultStartTime,
  formatDay,
  formatInstantDay,
  laterDateRange,
  startTimeOptions,
  timezoneLine,
  todayInZone,
  vendorCodeLabel
} from "./codeScheduling";
import { accessCodesQueryKey } from "./queryKeys";
import { LOCKBOX_CODE_TYPE_LABELS } from "./constants";
import { useNow } from "./useNow";

const RECIPIENT_NAME_MAX_LENGTH = 100;

const ERROR_MESSAGES: Record<string, string> = {
  lockbox_account_unassigned:
    "Assign a vendor account to this lockbox before generating a code. You can do that from the Lockboxes screen.",
  integration_not_connected:
    "This lockbox's vendor isn't connected. Add credentials under Settings → Integrations first.",
  invalid_duration: "Pick one of the offered durations and try again.",
  duplicate_request:
    "A code is already being generated for this lockbox. Give it a moment and try again.",
  sentrilock_no_code_returned:
    "SentriLock didn't return a code for this serial number. Check the serial number and the assigned account, then try again.",
  sentrilock_no_shackle_code_returned:
    "SentriLock didn't return a shackle code for this lockbox. Check the serial number and the assigned account, then try again.",
  shackle_code_unsupported:
    "Shackle codes are only available for SentriLock lockboxes.",
  codebox_no_code_returned:
    "Codebox didn't return a code for this serial number. Check the serial number and the assigned account, then try again.",
  rently_no_code_returned:
    "Rently didn't return a code for this serial number. Check the serial number and the assigned account, then try again.",
  igloohome_no_code_returned:
    "Igloohome didn't return a code for this lock. Check the device ID and the assigned account, then try again.",
  igloohome_hour_full:
    "This lock already has the most codes Igloohome allows starting in that hour. Pick a different start time and try again.",
  rently_device_type_missing:
    "Set this lockbox's device type before generating a code. You can do that from the Lockboxes screen.",
  invalid_recipient_name: `Keep "Who is it for?" to ${RECIPIENT_NAME_MAX_LENGTH} characters or fewer.`,
  future_start_unsupported:
    "This lockbox's vendor can't start a code on a later day. Switch to Now and try again.",
  invalid_start: "Pick a valid date and start time, then try again.",
  start_in_past: PAST_START_MESSAGE,
  start_too_far: "That date is too far ahead. Pick a date closer to today.",
  invalid_start_time: "Pick one of the offered start times and try again.",
  invalid_time_zone:
    "We couldn't read your computer's time zone. Check your system time zone, then try again."
};

// Only a Blue Lock honours the duration we ask for; Rently times the other two itself, so the
// picker is hidden for them and we say up front what they will actually get.
const RENTLY_DISCLOSURES: Record<RentlyDeviceType, string | null> = {
  blue_lock: null,
  orange_lockbox:
    "Rently Orange Lockbox codes work for the rest of today and can't be shortened or canceled once created.",
  smart_bolt:
    "Rently Smart Bolt codes can only be used once, expire 3 hours after you generate them, and can't be canceled."
};

const PAST_DATE_MESSAGE = "That date has passed. Pick today or a later date.";

// The audit row failed to save, so the code won't be in the list after the pop-up closes.
const unsavedCodeMessage = (code: string) =>
  `Code ${code} was created, but it couldn't be added to the list. Copy it now before you close this message.`;

const generateCodeErrorMessage = (error: unknown, lockbox: Lockbox) => {
  const key = getApiErrorKey(error);
  if (key === "start_in_past" && lockbox.code_scheduling?.all_day)
    return PAST_DATE_MESSAGE;
  if (key === "vendor_rejected_date") {
    const vendor = lockbox.vendor
      ? VENDOR_COMPANY_LABELS[lockbox.vendor]
      : "The vendor";
    return `${vendor} wouldn't create a code for that date. Try a date closer to today.`;
  }
  return key && ERROR_MESSAGES[key]
    ? ERROR_MESSAGES[key]
    : getErrorMessage(error);
};

const DURATION_LABELS: Record<number, string> = {
  30: "30 minutes",
  60: "1 hour",
  120: "2 hours",
  240: "4 hours"
};

const durationOptions = (durations: readonly number[]) =>
  durations.map((value) => ({
    label: DURATION_LABELS[value] ?? `${value} minutes`,
    value
  }));

const CODEBOX_NOW_NOTE =
  "Codebox codes work for the rest of today and can't be shortened or canceled once created.";
const IGLOOHOME_NOW_NOTE =
  "Igloohome codes work for 24 hours and can only be used once—they can't be shortened or canceled once created.";

// The yellow notes shown under Now: when the code starts, then the vendor's fixed window.
const nowNotes = ({
  lockbox,
  durationNote,
  isCodebox,
  rentlyDisclosure
}: {
  lockbox: Lockbox;
  durationNote: string | null;
  isCodebox: boolean;
  rentlyDisclosure: string | null;
}) =>
  [
    durationNote,
    isCodebox ? CODEBOX_NOW_NOTE : null,
    rentlyDisclosure,
    lockbox.vendor === "igloohome" ? IGLOOHOME_NOW_NOTE : null
  ].filter((note): note is string => Boolean(note));

const NowNotes = ({
  notes,
  startsOnGenerateText
}: {
  notes: string[];
  startsOnGenerateText: string | null;
}) => (
  <>
    {notes.map((note) => (
      <div key={note} className="rounded-lg bg-warning-50 p-4">
        <p className="text-sm font-medium text-warning-700">{note}</p>
      </div>
    ))}
    <Show if={Boolean(startsOnGenerateText)}>
      <p className="text-sm text-content-400">{startsOnGenerateText}</p>
    </Show>
  </>
);

const CodeWindowNote = ({ summary }: { summary: string | null }) => (
  <Show if={Boolean(summary)}>
    <div className="space-y-1 rounded-lg bg-background-900 p-4">
      <p className="text-sm font-medium text-content-500">Code window</p>
      <p className="text-sm text-content-1000">{summary}</p>
    </div>
  </Show>
);

// Date and Start time side by side (Date alone for all-day vendors), with the time zone line
// right under them, as in the design.
const LaterStartFields = ({
  date,
  minDate,
  maxDays,
  showStartTime,
  timeOptions,
  time,
  timeError,
  timezoneText,
  onDateChange,
  onTimeChange
}: {
  date: string;
  minDate: string;
  maxDays: number;
  showStartTime: boolean;
  timeOptions: StartTimeOption[];
  time: string | null;
  timeError: string | null;
  timezoneText: string;
  onDateChange: (date: string) => void;
  onTimeChange: (time: string) => void;
}) => (
  <div className="space-y-2">
    <div className={showStartTime ? "grid grid-cols-2 gap-3" : undefined}>
      <CodeDateField
        value={date}
        minDate={minDate}
        maxDays={maxDays}
        onChange={onDateChange}
      />
      <Show if={showStartTime}>
        {/* Grid cell: Select renders its label and control as siblings. */}
        <div>
          <Select
            id="code-start-time"
            label="Start time"
            hideOptional
            options={timeOptions}
            value={time}
            searchable={false}
            clearable={false}
            error={timeError ?? undefined}
            showErrorMessage
            legacyErrorMargins={false}
            onChange={(e) => onTimeChange(String(e.target.value))}
          />
        </div>
      </Show>
    </div>
    <p className="text-xs text-content-400">{timezoneText}</p>
  </div>
);

type CodeMode = "now" | "later";

const MODE_TABS: { value: CodeMode; label: string }[] = [
  { value: "now", label: "Now" },
  { value: "later", label: "Later" }
];

const CLOCK_TICK_MS = 30_000;

const codeDurations = (lockbox: Lockbox): readonly number[] | null => {
  if (lockbox.code_scheduling) return lockbox.code_scheduling.durations;
  const legacyNeedsDuration =
    lockbox.vendor === "sentrilock" ||
    (lockbox.vendor === "rently" && lockbox.device_type === "blue_lock");
  return legacyNeedsDuration ? SENTRILOCK_CODE_DURATIONS : null;
};

const withPinnedTime = (
  options: StartTimeOption[],
  value: string | null,
  date: string,
  zone: string
) =>
  value && !options.some((option) => option.value === value)
    ? [
        {
          value,
          label: combineStart(date, value, zone).toFormat("h:mm a")
        },
        ...options
      ]
    : options;

const DEFAULT_CODE_TYPE: LockboxCodeType = "access";

const CODE_TYPE_TABS: Tab<LockboxCodeType>[] = [
  { value: "access", label: LOCKBOX_CODE_TYPE_LABELS.access },
  { value: "shackle", label: LOCKBOX_CODE_TYPE_LABELS.shackle }
];

const CODE_TYPE_DESCRIPTIONS: Record<LockboxCodeType, string> = {
  access: "Opens the key compartment.",
  shackle:
    "Releases the shackle so you can put the lockbox on a door or take it off."
};

const DURATION_NOTE = "Starts as soon as you generate it";

const SENTRILOCK_NOTES: Record<LockboxCodeType, string> = {
  access: DURATION_NOTE,
  shackle:
    "Starts as soon as you generate it. Anyone with this code can take the lockbox off the door. Share it only with whoever is installing or removing it."
};

const codeDurationNote = ({
  needsDuration,
  isSentrilock,
  codeType
}: {
  needsDuration: boolean;
  isSentrilock: boolean;
  codeType: LockboxCodeType;
}) => {
  if (!needsDuration) return null;
  return isSentrilock ? SENTRILOCK_NOTES[codeType] : DURATION_NOTE;
};

const codeTypeParam = (
  lockbox: Lockbox,
  codeType: LockboxCodeType
): Pick<GenerateLockboxCodePayload, "code_type"> =>
  lockbox.vendor === "sentrilock" ? { code_type: codeType } : {};

export const GenerateLockboxCodeModal = ({
  opened,
  onClose,
  onGenerated,
  lockbox,
  timezone
}: {
  opened: boolean;
  onClose: () => void;
  onGenerated: (result: AccessCode) => void;
  lockbox: Lockbox;
  timezone: CodeTimezone;
}) => {
  const queryClient = useQueryClient();
  const now = useNow({ maxDelayMs: CLOCK_TICK_MS });
  const { zone } = timezone;
  const scheduling = lockbox.code_scheduling ?? null;
  const canSchedule = Boolean(scheduling?.supported);
  const startsOnGenerate = scheduling?.supported === false;

  const isSentrilock = lockbox.vendor === "sentrilock";
  const isIgloohome = lockbox.vendor === "igloohome";
  const isRently = lockbox.vendor === "rently";
  const isCodebox = !isSentrilock && !isIgloohome && !isRently;
  const durations = codeDurations(lockbox);
  const needsDuration = Boolean(durations?.length);
  const rentlyDisclosure =
    isRently && lockbox.device_type
      ? RENTLY_DISCLOSURES[lockbox.device_type]
      : null;

  const [mode, setMode] = useState<CodeMode>("now");
  const [date, setDate] = useState<string | null>(null);
  const [startTime, setStartTime] = useState<string | null>(null);
  const [duration, setDuration] = useState<number | null>(null);
  const [recipientName, setRecipientName] = useState("");
  const [startError, setStartError] = useState<string | null>(null);

  const isLater = canSchedule && mode === "later";
  const isAllDay = Boolean(scheduling?.all_day);
  const today = todayInZone(zone, now);
  const dateRange = scheduling
    ? laterDateRange({ zone, scheduling, now })
    : { minDate: today, maxDays: 0 };
  const selectedDate = date ?? dateRange.minDate;
  const selectedDuration = duration ?? durations?.[0] ?? null;

  const offeredTimes = useMemo(
    () =>
      scheduling && !scheduling.all_day
        ? startTimeOptions({
            date: selectedDate,
            zone,
            stepMinutes: scheduling.start_step_minutes,
            now
          })
        : [],
    [scheduling, selectedDate, zone, now]
  );
  const selectedTime = startTime ?? defaultStartTime(offeredTimes, zone, now);
  const timeOffered = offeredTimes.some(
    (option) => option.value === selectedTime
  );
  const timeOptions = withPinnedTime(
    offeredTimes,
    selectedTime,
    selectedDate,
    zone
  );
  const start = selectedTime
    ? combineStart(selectedDate, selectedTime, zone)
    : null;
  const startFieldError =
    startError ??
    (isLater && !isAllDay && !timeOffered ? PAST_START_MESSAGE : null);

  const summary =
    isLater && scheduling
      ? codeWindowSummary({
          lockbox,
          scheduling,
          zone,
          date: selectedDate,
          start,
          durationMinutes: selectedDuration
        })
      : null;

  const [codeType, setCodeType] = useState<LockboxCodeType>(DEFAULT_CODE_TYPE);

  const durationNote = codeDurationNote({
    needsDuration,
    isSentrilock,
    codeType
  });

  const resetForm = () => {
    setMode("now");
    setDate(null);
    setStartTime(null);
    setDuration(null);
    setRecipientName("");
    setStartError(null);
    setCodeType(DEFAULT_CODE_TYPE);
  };

  const { mutate: generate, isPending } = useMutation({
    mutationKey: ["LOCKBOX", "GENERATE_CODE", "CREATE", lockbox.id],
    mutationFn: (payload: GenerateLockboxCodePayload) =>
      lockboxesApi.generateCode(lockbox.id, payload),
    onSuccess: (result, payload) => {
      // A code whose audit row failed to save comes back with id null. It has no stable key to
      // cache under and the refetch won't return it, so hand the code over in a toast that stays.
      if (result.id == null)
        toast.warn(unsavedCodeMessage(result.code), { autoClose: false });
      else
        queryClient.setQueryData<AccessCodesResponse>(
          accessCodesQueryKey(lockbox.id),
          (prev) => ({
            time_zone: prev?.time_zone ?? lockbox.timezone ?? null,
            access_codes: [
              ...(prev?.access_codes ?? []).filter(
                (item) => item.source !== result.source || item.id !== result.id
              ),
              result
            ]
          })
        );
      queryClient.invalidateQueries({ queryKey: ["LOCKBOXES"] });
      queryClient.invalidateQueries({
        queryKey: ["LOCKBOX_ACTIVITY_LOG", lockbox.id]
      });
      if (payload.start_date || payload.starts_at) {
        const day = payload.start_date
          ? formatDay(payload.start_date, zone)
          : formatInstantDay(result.valid_from, zone);
        toast.success(`Code scheduled for ${day}`);
      }
      onGenerated(result);
      resetForm();
    },
    onError: (err) => {
      if (getApiErrorKey(err) === "start_in_past" && !isAllDay) {
        setStartError(PAST_START_MESSAGE);
        return;
      }
      toast.error(generateCodeErrorMessage(err, lockbox));
    }
  });

  const handleGenerate = () => {
    const startPassed =
      !start?.isValid || start.toMillis() <= DateTime.now().toMillis();
    if (isLater && !isAllDay && startPassed) {
      setStartError(PAST_START_MESSAGE);
      return;
    }
    generate({
      ...buildGeneratePayload({
        later: isLater,
        scheduling,
        timezone,
        date: selectedDate,
        start,
        durationMinutes: needsDuration ? selectedDuration : null,
        recipientName
      }),
      ...codeTypeParam(lockbox, codeType)
    });
  };

  const handleClose = () => {
    if (isPending) return;
    resetForm();
    onClose();
  };

  return (
    <Modal
      zIndex={120}
      opened={opened}
      onClose={handleClose}
      panelClassName="relative max-w-[366px] w-full border border-alpha-100 p-6 space-y-4 shadow-lg"
    >
      <p className="text-center text-xl font-bold text-content-1000">
        Generate lockbox code
      </p>

      <Show if={canSchedule}>
        <Tabs<CodeMode>
          tabs={MODE_TABS}
          active={mode}
          onChange={(next) => {
            setMode(next);
            setStartError(null);
          }}
          tabStyle="outline"
          size="sm"
          fullWidthTabs
        />
      </Show>

      <Show if={isSentrilock}>
        <div>
          <Label size={LabelSize.SM} as="span" className="block">
            <span className="font-medium text-content-1000">
              What do you need?
            </span>
          </Label>
          <Tabs
            size="sm"
            tabStyle="outline"
            fullWidthTabs
            // Locked while a code is being generated, so the pick can't change under the request.
            tabs={CODE_TYPE_TABS.map((tab) => ({
              ...tab,
              disabled: isPending
            }))}
            active={codeType}
            onChange={setCodeType}
          />
          <p className="mt-2 text-sm text-content-400">
            {CODE_TYPE_DESCRIPTIONS[codeType]}
          </p>
        </div>
      </Show>

      <div className="space-y-4">
        <BaseInput
          id="code-recipient-name"
          label="Who is it for?"
          placeholder="e.g. Smith Realty"
          maxLength={RECIPIENT_NAME_MAX_LENGTH}
          value={recipientName}
          onChange={setRecipientName}
        />

        <Show if={isLater}>
          <LaterStartFields
            date={selectedDate}
            minDate={dateRange.minDate}
            maxDays={dateRange.maxDays}
            showStartTime={!isAllDay}
            timeOptions={timeOptions}
            time={selectedTime}
            timeError={startFieldError}
            timezoneText={timezoneLine(
              timezone,
              start?.isValid ? start : DateTime.fromISO(selectedDate, { zone })
            )}
            onDateChange={(next) => {
              setDate(next);
              setStartError(null);
            }}
            onTimeChange={(next) => {
              setStartTime(next);
              setStartError(null);
            }}
          />
        </Show>

        <Show if={needsDuration && !(isLater && isAllDay)}>
          <Select
            id="code-duration"
            label="How long should the code work?"
            hideOptional
            options={durationOptions(durations ?? [])}
            value={selectedDuration}
            searchable={false}
            clearable={false}
            onChange={(e) => {
              const parsed = Number(e.target.value);
              if (durations?.includes(parsed)) setDuration(parsed);
            }}
          />
        </Show>

        {isLater ? (
          <CodeWindowNote summary={summary} />
        ) : (
          <NowNotes
            notes={nowNotes({
              lockbox,
              durationNote,
              isCodebox,
              rentlyDisclosure
            })}
            startsOnGenerateText={
              startsOnGenerate
                ? `${vendorCodeLabel(lockbox)} codes start when you generate them.`
                : null
            }
          />
        )}

        {/* One "Generate" for both code types: the switch and the note already say which kind, and a
            longer label doesn't fit half of the 366px pop-up. */}
        <div className="flex w-full gap-2">
          <Button className="w-full" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            className="w-full"
            variant={ButtonVariant.PRIMARY}
            onClick={handleGenerate}
            loading={isPending}
            disabled={isPending}
          >
            Generate
          </Button>
        </div>
      </div>
    </Modal>
  );
};
